import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import horizontalLogo from '../../assets/brand/pr-a-cairn-horizontal.svg';
import verticalLogo from '../../assets/brand/pr-a-cairn-vertical.svg';
import negativeLogo from '../../assets/brand/pr-a-cairn-negativo.svg';
import endorsedLogo from '../../assets/brand/pr-a-endosso-justask.svg';
import monochromeLogo from '../../assets/brand/pr-a-cairn-monocromatico.svg';

/**
 * Logo variants, built from the official "people rock by JustASK" artwork:
 * - `horizontal`: main version (navbars, headers)
 * - `vertical`: centred, stacked (login / sign-up cards)
 * - `negative`: cream ink with the "by JustASK" endorsement, for dark surfaces
 * - `endorsed`: with the "by JustASK" endorsement (footer, institutional contexts)
 * - `monochrome`: single ink, for one-colour print or stamps
 */
export type BrandLogoVariant = 'horizontal' | 'vertical' | 'negative' | 'endorsed' | 'monochrome';

const LOGO_SOURCES: Record<BrandLogoVariant, string> = {
  horizontal: horizontalLogo,
  vertical: verticalLogo,
  negative: negativeLogo,
  endorsed: endorsedLogo,
  monochrome: monochromeLogo,
};

export interface BrandLogoProps {
  /** Rendered height in px; the width follows the logo's aspect ratio. */
  height?: number;
  variant?: BrandLogoVariant;
  sx?: SxProps<Theme>;
}

/**
 * People Rock logo, from the design system's official SVGs.
 * The protection area around the logo is the height of its top stone: keep clear space around it.
 */
export default function BrandLogo({ height = 36, variant = 'horizontal', sx }: BrandLogoProps) {
  return (
    <Box
      component="img"
      src={LOGO_SOURCES[variant]}
      alt="People Rock"
      sx={[{ height, width: 'auto', display: 'block' }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
